import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { unstable_batchedUpdates } from "react-dom";
import { Dropdown, Spin } from "antd";
import InfiniteScroll from "react-infinite-scroll-component";
import { httpService } from "../../base/services/httpService.service";
import AuthService from "../../base/services/authentication.service";
import { useTranslation } from "../../i18n/LanguageContext";
import { formatRelativeDate } from "../../base/utils/dateFormat";
import { EmptyBox } from "../../assets/icons";
import { NotificationContext } from "../../../_metronic/layout/components/subheader/notificationContext/NotificationContext";
import { useNotificationRedirect } from "../../../_metronic/layout/components/subheader/notificationContext/NotificationRedirect";
import { useNotificationStore } from "../../../_metronic/layout/components/subheader/components/useNotificationStore";
import {
   NotificationTypeIcon,
   getNotificationRoleColor,
   getNotificationTitle
} from "./NotificationTypeIcon";
import { getNotificationBody, useCompletedByNames } from "./completedBy";
import "./notifications.scss";

const PAGE_SIZE = 10;
const SCROLL_TARGET_ID = "kap-notifications-scroll";
// How long a 401'd request waits for the interceptor's token refresh before retrying.
const TOKEN_POLL_MS = 250;
const TOKEN_WAIT_MS = 5000;

/**
 * Full-page notification list (All / Unread), reached from the sidebar. Uses the same
 * endpoints as the header bell and keeps the bell's badge and list in sync when
 * notifications are read here.
 */
function NotificationsPage() {
   const { t, language } = useTranslation();
   const {
      user,
      unreadCount,
      setPopoverUnreadCount,
      setPopoverUnreadCountAll,
      setPopoverIncCount
   } = useContext(NotificationContext);
   const newNotification = useNotificationStore((state) => state.newNotification);
   const roleColor = useMemo(getNotificationRoleColor, []);
   const notificationRedirect = useNotificationRedirect();

   const [tab, setTab] = useState("all");
   const [items, setItems] = useState([]);
   const completedByNames = useCompletedByNames(items);
   const [pageNumber, setPageNumber] = useState(1);
   const [hasMore, setHasMore] = useState(false);
   const [loading, setLoading] = useState(true);
   const [allCount, setAllCount] = useState(0);
   const [markingAll, setMarkingAll] = useState(false);
   // Responses from an earlier tab/language can land after a newer request; only the
   // latest one may write to the list.
   const latestRequest = useRef(0);
   // A next-page request is in flight; the scroll handler and the fill-the-list effect
   // below can both ask for the same page.
   const loadingMore = useRef(false);
   const listRef = useRef(null);

   const loadPage = useCallback(
      (page, activeTab, isRetry = false) => {
         if (!user?.id) return;
         if (page > 1 && !isRetry) {
            if (loadingMore.current) return;
            loadingMore.current = true;
         } else {
            // A fresh list (tab/language change) supersedes any next-page request.
            loadingMore.current = false;
         }
         const requestId = ++latestRequest.current;
         const tokenUsed = AuthService.getAccessToken();
         if (page === 1) setLoading(true);

         // On a full page load the first requests can go out with a token that is about
         // to be replaced and come back 401. The http interceptor refreshes the token but
         // doesn't retry the request, so wait for the new token and retry once ourselves.
         const retryAfterRefresh = (error) => {
            if (isRetry || error?.response?.status !== 401) return false;
            let waited = 0;
            const timer = setInterval(() => {
               waited += TOKEN_POLL_MS;
               const refreshed = AuthService.getAccessToken() !== tokenUsed;
               if (!refreshed && waited < TOKEN_WAIT_MS) return;
               clearInterval(timer);
               if (requestId !== latestRequest.current) {
                  if (page > 1) loadingMore.current = false;
                  return;
               }
               loadPage(page, activeTab, true);
            }, TOKEN_POLL_MS);
            return true;
         };

         const stopLoading = () => {
            if (page > 1) loadingMore.current = false;
            if (requestId !== latestRequest.current) return;
            setLoading(false);
            setHasMore(false);
         };

         httpService
            .get(
            `/Notifications/all/${user.id}?pageNumber=${page}&pageSize=${PAGE_SIZE}${
               activeTab === "unread" ? "&unRead=true" : ""
            }`,
            (res) => {
               if (page > 1) loadingMore.current = false;
               if (requestId !== latestRequest.current) return;
               const data = res?.data || [];
               // React 16 doesn't batch updates outside event handlers; without this the
               // new rows render before pageNumber changes and the fill-the-list effect
               // requests the same page again.
               unstable_batchedUpdates(() => {
                  setItems((prev) => (page === 1 ? data : [...prev, ...data]));
                  setPageNumber(page);
                  setHasMore(page < (res?.metaData?.pageCount || 0));
                  if (activeTab === "all") setAllCount(res?.metaData?.totalItemCount || 0);
                  setLoading(false);
               });
            },
            stopLoading
            )
            .catch((error) => {
               if (!retryAfterRefresh(error)) stopLoading();
            });
      },
      [user?.id]
   );

   // Re-fetch on language change too: titles and bodies come back localized.
   useEffect(() => {
      loadPage(1, tab);
   }, [loadPage, tab, language.code]);

   // Infinite scroll only loads more on a scroll event. On tall screens the first page
   // doesn't fill the list, so there is nothing to scroll: keep loading until it does.
   useEffect(() => {
      const list = listRef.current;
      if (loading || !hasMore || !list) return;
      if (list.scrollHeight <= list.clientHeight) loadPage(pageNumber + 1, tab);
   }, [items, loading, hasMore, pageNumber, tab, loadPage]);

   // A real-time notification arrived (the bell's SignalR handler sets this).
   useEffect(() => {
      if (!newNotification) return;
      if (tab === "unread") setAllCount((count) => count + 1);
      loadPage(1, tab);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [newNotification]);

   const updateBellList = (update) => {
      const { notificationData, setNotificationData } = useNotificationStore.getState();
      setNotificationData(notificationData?.map(update));
   };

   const markRead = (notification) => {
      if (notification.isRead) return;
      const setRead = (isRead) => (item) =>
         item.id === notification.id ? { ...item, isRead } : item;

      // Optimistic: the row, the bell badge and the bell list flip now and roll back
      // if the request fails.
      setItems((prev) => prev.map(setRead(true)));
      setPopoverUnreadCount();
      updateBellList(setRead(true));

      const rollback = () => {
         setItems((prev) => prev.map(setRead(false)));
         setPopoverIncCount();
         updateBellList(setRead(false));
      };
      httpService
         .post("/Notifications/read", { id: notification.id }, undefined, rollback)
         .catch(rollback);
   };

   const openNotification = (notification) => {
      markRead(notification);
      notificationRedirect(
         notification.notificationType,
         notification.entityId,
         notification.workspaceId,
         notification.notificationBody
      );
   };

   const markAllRead = () => {
      if (!user?.id || markingAll) return;
      setMarkingAll(true);
      const stop = () => setMarkingAll(false);
      httpService
         .post(
            "/Notifications/read/all",
            { userId: user.id },
            () => {
               setPopoverUnreadCountAll();
               updateBellList((item) => ({ ...item, isRead: true }));
               setMarkingAll(false);
               loadPage(1, tab);
            },
            stop
         )
         .catch(stop);
   };

   const canMarkAll = unreadCount > 0 && !markingAll;

   const tabs = [
      { key: "all", label: t("all-tab-label"), count: allCount },
      { key: "unread", label: t("unread-tab-label"), count: unreadCount }
   ];

   return (
      <div className="kap-notifications">
         <div className="kap-notifications__card">
            <div className="kap-notifications__header">
               <h1 className="kap-notifications__title">{t("notifications-page-title")}</h1>
               <button
                  type="button"
                  className="kap-notifications__mark-all"
                  onClick={markAllRead}
                  disabled={!canMarkAll}
               >
                  {markingAll ? <Spin size="small" /> : <CheckIcon />}
                  {t("mark-all-as-read-button")}
               </button>
            </div>

            <div className="kap-notifications__tabs" role="tablist">
               {tabs.map(({ key, label, count }) => (
                  <button
                     key={key}
                     type="button"
                     role="tab"
                     aria-selected={tab === key}
                     className={`kap-notifications__tab${tab === key ? " is-active" : ""}`}
                     onClick={() => setTab(key)}
                  >
                     {label}
                     <span className="kap-notifications__count">{count}</span>
                  </button>
               ))}
            </div>

            <div id={SCROLL_TARGET_ID} ref={listRef} className="kap-notifications__list">
               {loading ? (
                  <div className="kap-notifications__state">
                     <Spin />
                  </div>
               ) : items.length === 0 ? (
                  <div className="kap-notifications__state">
                     <img src={EmptyBox} alt="" width={64} height={40} />
                     <p className="kap-notifications__empty-title">
                        {t(
                           tab === "unread"
                              ? "notifications-unread-empty-title"
                              : "notifications-empty-title"
                        )}
                     </p>
                     <p className="kap-notifications__empty-text">{t("notifications-empty-text")}</p>
                  </div>
               ) : (
                  <InfiniteScroll
                     dataLength={items.length}
                     hasMore={hasMore}
                     next={() => loadPage(pageNumber + 1, tab)}
                     loader={
                        <div className="kap-notifications__loader">
                           <Spin size="small" />
                        </div>
                     }
                     scrollableTarget={SCROLL_TARGET_ID}
                  >
                     <ul className="kap-notifications__items">
                        {items.map((notification) => (
                           <NotificationRow
                              key={notification.id}
                              notification={notification}
                              completedByNames={completedByNames}
                              onOpen={openNotification}
                              onMarkRead={markRead}
                              roleColor={roleColor}
                              t={t}
                           />
                        ))}
                     </ul>
                  </InfiniteScroll>
               )}
            </div>
         </div>
      </div>
   );
}

function NotificationRow({ notification, completedByNames, onOpen, onMarkRead, roleColor, t }) {
   const { isRead } = notification;

   const handleKeyDown = (e) => {
      if (e.target !== e.currentTarget) return;
      if (e.key === "Enter" || e.key === " ") {
         e.preventDefault();
         onOpen(notification);
      }
   };

   return (
      <li
         className={`kap-notifications__item${isRead ? "" : " is-unread"}`}
         role="button"
         tabIndex={0}
         onClick={() => onOpen(notification)}
         onKeyDown={handleKeyDown}
      >
         <span className="kap-notifications__dot" aria-hidden="true" />
         <span
            className="kap-notifications__icon"
            aria-hidden="true"
            // 8% tint of the role colour behind the glyph (admin: ~#F1F6FF as in the design).
            style={{ color: roleColor, backgroundColor: `${roleColor}14` }}
         >
            <NotificationTypeIcon type={notification.notificationType} />
         </span>
         <span className="kap-notifications__content">
            <span className="kap-notifications__item-title">{getNotificationTitle(notification, t)}</span>
            <span className="kap-notifications__item-body">
               {getNotificationBody(notification, completedByNames, t)}
            </span>
         </span>
         <span className="kap-notifications__meta">
            {/* Only unread rows have an action; read rows keep the space so times line up. */}
            <span
               className={`kap-notifications__menu${isRead ? " is-hidden" : ""}`}
               onClick={(e) => e.stopPropagation()}
               onKeyDown={(e) => e.stopPropagation()}
            >
               <Dropdown
                  trigger={["click"]}
                  placement="bottomRight"
                  disabled={isRead}
                  menu={{
                     items: [{ key: "read", label: t("mark-as-read-label") }],
                     onClick: () => onMarkRead(notification)
                  }}
               >
                  <button
                     type="button"
                     className="kap-notifications__menu-button"
                     aria-label={t("notification-actions-label")}
                     tabIndex={isRead ? -1 : 0}
                  >
                     <KebabIcon />
                  </button>
               </Dropdown>
            </span>
            <span className="kap-notifications__time">
               {formatRelativeDate(notification.createdDate)}
            </span>
         </span>
      </li>
   );
}

const CheckIcon = () => (
   <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path
         d="M2.5 7.5L5.5 10.5L11.5 3.5"
         stroke="currentColor"
         strokeWidth="1.4"
         strokeLinecap="round"
         strokeLinejoin="round"
      />
   </svg>
);

const KebabIcon = () => (
   <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
      <circle cx="8" cy="3" r="1.4" />
      <circle cx="8" cy="8" r="1.4" />
      <circle cx="8" cy="13" r="1.4" />
   </svg>
);

export default NotificationsPage;
